import {
  Component,
  OnInit,
  ViewChild,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core'
import { FprTemplate } from '../../../../types/fprTemplates'
import {
  MatPaginatorIntl,
  MatPaginator,
  MatPaginatorModule,
} from '@angular/material/paginator'
import { MatTableDataSource, MatTableModule } from '@angular/material/table'
import { PaginatorIntl } from '../../../intl/paginator-intl'
import { FprTemplatesService } from '../../../../services/fprTemplate.service'
import { Dialog } from '@angular/cdk/dialog'
import { MatBottomSheet } from '@angular/material/bottom-sheet'
import { Router, RouterModule } from '@angular/router'
import { firstValueFrom } from 'rxjs'
import { CommonService } from '../../../../services/common.service'
import { DialogService } from '../../../../services/dialog.service'
import { ErrorService } from '../../../../services/error.service'
import { SpinnerService } from '../../../../services/spinner.service'
import { TranslationService } from '../../../../services/translation.service'
import { ApiError } from '../../../../types/apiError'
import {
  ConfirmDialogData,
  ConfirmDialogComponent,
} from '../../../shared/confirm-dialog/confirm-dialog.component'

import { FormsModule } from '@angular/forms'
import { MatButtonModule } from '@angular/material/button'
import { MatChipsModule } from '@angular/material/chips'
import { MatInputModule } from '@angular/material/input'
import { MatSelectModule } from '@angular/material/select'
import { FprFieldMappingSheetComponent } from './fpr-field-mapping-sheet/fpr-field-mapping-sheet.component'

@Component({
  selector: 'app-fpr-template-list',
  imports: [
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatTableModule,
    MatPaginatorModule,
    FormsModule,
    MatChipsModule,
    RouterModule,
  ],
  providers: [
    {
      provide: MatPaginatorIntl,
      useClass: PaginatorIntl,
    },
  ],
  templateUrl: './fpr-template-list.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './fpr-template-list.component.scss',
})
export class FprTemplateListComponent implements OnInit {
  translationService = inject(TranslationService)
  private dialog = inject(Dialog)
  private dialogService = inject(DialogService)
  private errorService = inject(ErrorService)
  private spinnerService = inject(SpinnerService)
  private fprTemplateService = inject(FprTemplatesService)
  private _bottomSheet = inject(MatBottomSheet)
  private router = inject(Router)
  private commonService = inject(CommonService)

  fprTemplates: Array<FprTemplate> = []
  displayedColumns: Array<string> = ['name', 'actions']
  dataSource = new MatTableDataSource<FprTemplate>([])

  @ViewChild(MatPaginator) set paginator(mp: MatPaginator) {
    this.dataSource.paginator = mp
  }

  async ngOnInit(): Promise<void> {
    await this.getResults()
  }

  /**
   * Retrieves templates for a company
   */
  async getResults() {
    try {
      this.spinnerService.startSpinner()
      const response = await firstValueFrom(this.fprTemplateService.getAll())
      this.fprTemplates = response
      this.dataSource.data = this.fprTemplates
      this.spinnerService.stopSpinner()
    } catch (err) {
      this.spinnerService.stopSpinner()
      const apiErr = err as ApiError
      this.errorService.handleErr(this.dialogService, apiErr, this.dialog)
    }
  }

  /**
   * Opens bottom sheet to show csv field mappings
   */
  showFieldMappings(fprTemplate: FprTemplate) {
    this._bottomSheet.open(FprFieldMappingSheetComponent, {
      data: {
        fprTemplate: fprTemplate,
      },
    })
  }

  /**
   * Navigates user to edit template screen
   * @param {string} id The id of the template to edit
   */
  goToEdit(id: string) {
    this.router.navigate(['settings', 'fpr-templates', id, 'edit'])
  }

  /**
   * Opens a dialog to prompt the user to delete a csv template. On succesful deletion, removes the deleted element from the list
   * @param {string} id Id of the template
   * @param {string} name Name of the template
   */
  async delete(id: string, name: string) {
    try {
      const dialogData: ConfirmDialogData = {
        header: this.translationService.getConfirmActionHeaderText(),
        message: this.translationService.getDeleteTemplateMessageText(name),
        onCancel: () => {},
      }

      const dialogRef = this.dialog.open<boolean>(ConfirmDialogComponent, {
        data: dialogData,
      })

      dialogRef.closed.subscribe(async (confirmed) => {
        if (confirmed) {
          this.spinnerService.startSpinner()
          await firstValueFrom(this.fprTemplateService.delete(id))
          this.spinnerService.stopSpinner()

          // we can then remove the deleted template from fprTemplates array and update the dataSource
          const index = this.fprTemplates.findIndex(
            (template) => template.id === id,
          )
          if (index !== -1) {
            this.fprTemplates.splice(index, 1)
            this.dataSource.data = [...this.fprTemplates]
          }

          this.dialogService.showDialog(
            this.dialog,
            this.translationService.getFprTemplatesText(),
            this.translationService.getTemplateDeletedText(),
          )
        }
      })
    } catch (err) {
      this.spinnerService.stopSpinner()
      const apiErr = err as ApiError
      this.errorService.handleErr(this.dialogService, apiErr, this.dialog)
    }
  }

  /**
   * Downloads an Excel template
   * @param {string} id Id of the template
   * @param {string} name Name of the template
   */
  async download(id: string, name: string) {
    try {
      const fprFileName = name + '.xlsx'

      this.spinnerService.startSpinner()
      const result = await firstValueFrom(this.fprTemplateService.download(id))

      this.commonService.downloadBase64File(result, fprFileName)

      this.spinnerService.stopSpinner()
    } catch (err) {
      this.spinnerService.stopSpinner()
      const apiErr = err as ApiError
      this.errorService.handleErr(this.dialogService, apiErr, this.dialog)
    }
  }
}
